import { TagIcon } from "@/lib/tag-utils";
import { Person, Reservation, Service, ServiceServiceGroups, TenantBusiness } from "@turizi/shared";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ServiceTableGeneral from "./service-tables/service-table-general";
import { usePublicServices, useServiceCategories, useServiceGroups, useServices } from "@/hooks/api-hooks";
import { Button, NumberInput, Skeleton } from "@mantine/core";
import ServiceDatePopover from "./service-date-popover";
import { useEffect, useState, useMemo, useCallback } from "react";
import { IconSearch, IconUsersGroup } from "@tabler/icons-react";
import { SkeletonListWithFilters } from "./snippets";
import PaxSelector from "./paxs-selector-with-source-popover";
import { appendWebTitle, getUrlParam, todayStart, tomorrowStart, updateUrlParams } from "@/lib/format-utils";
import ServiceGroupsList from "./service-groups-list";
import { localTenant } from "@/services/session-service";

type Props = {
    onSelect?: (service: Service, persons: Person[], dates: [string, string], discount: any) => void;
    showAllSelfAttrs?: boolean;
    reservation?: Reservation;
    availability?: boolean;
    hideServiceGroups?: boolean;
    publicPage?: boolean;
    client?: TenantBusiness
}

export default function ServicesListWithFilters({
    onSelect,
    reservation,
    showAllSelfAttrs = true,
    availability = false,
    hideServiceGroups = false,
    publicPage = false,
    client,
}: Props) {
    const { t } = useTranslation();
    const tenant = localTenant();
    const tenant_id = tenant?.id;

    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get('category') || null);

    appendWebTitle(t(`${selectedCategory}_plural`, ''));

    const [dates, setDates] = useState<[string | null, string | null]>([null, null]);
    const [persons, setPersons] = useState<Person[]>([]);
    const [pax_count, setPaxCount] = useState<number | null>(null);
    const [searchData, setSearchData] = useState({
        tenant_id,
        category: selectedCategory,
        start_day: undefined,
        end_day: undefined,
        pax_count: undefined,
    });

    const funcToUse = publicPage ? usePublicServices : useServices;

    // all Tenant's categories, services and services-groups
    const {
        isLoading: isLoadingCategories,
        data: categoriesData
    } = useServiceCategories(
        { tenant_id }
    );

    const {
        isLoading,
        isFetching,
        data
    } = funcToUse(
        searchData,
        { enabled: !!searchData.category }
    );
    const {
        isLoading: isLoadingServiceGroups,
        data: data2
    } = useServiceGroups(
        { tenant_id },
        { enabled: !hideServiceGroups && !publicPage }
    );

    const handleCategoryClick = useCallback((tag: string) => {
        setSelectedCategory(tag);
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set('category', tag);
            return newParams;
        });
    }, [setSearchParams]);

    const categories = useMemo(() =>
        (categoriesData?.data || [])
            .filter(Boolean)
            .sort((a, b) => {
                const rankA = a.rank ?? Infinity;
                const rankB = b.rank ?? Infinity;
                return rankA - rankB;
            })
            .map(category => ({
                value: category.category,
                label: t(category.category + '_plural'),
            }))
        , [categoriesData?.data, t]);

    const serviceGroups = useMemo(() => data2?.data || [], [data2?.data]);
    const services = useMemo(() =>
        (data?.data || []).sort((a, b) => (b.rank || 0) - (a.rank || 0))
        , [data?.data]);

    // Update search data when category changes
    useEffect(() => {
        const categoryFromUrl = searchParams.get('category');
        if (categoryFromUrl && categoryFromUrl !== selectedCategory) {
            setSelectedCategory(categoryFromUrl);
        }
    }, [searchParams, selectedCategory]);

    useEffect(() => {
        setSearchData(prev => ({
            ...prev,
            category: selectedCategory,
        }));
    }, [selectedCategory, tenant_id]);

    // if no category is selected, set the first category
    useEffect(() => {
        if (!selectedCategory && categories.length > 0) {
            setSelectedCategory(categories[0].value);
        }
    }, [categories]);

    const reservationPersons = useMemo(() => {
        if (!reservation) return [];
        if (reservation.pax_groups && reservation.pax_groups.length > 0) {
            return reservation.pax_groups.flatMap((g) => g.persons || []);
        }
        return [];
    }, [reservation]);

    // Initialize dates and persons from URL or reservation
    useEffect(() => {
        if (reservation) {
            setDates([reservation?.start_day, reservation?.end_day]);
            setPersons(reservationPersons);
        } else {
            const pax_count_url = getUrlParam('pax_count', null);
            setDates([getUrlParam('start', todayStart()), getUrlParam('end', tomorrowStart())]);
            setPaxCount(pax_count_url ? Number(pax_count_url) : null);
            setPersons([]);
        }
    }, [reservation, reservationPersons]);

    useEffect(() => {
        updateUrlParams({
            start: dates[0] || todayStart(),
            end: dates[1] || tomorrowStart(),
        });
    }, []);

    return (
        <>
            <div>

                <div className="flex gap-2 pb-4 overflow-x-auto">
                    {categories.map(category => (
                        <TagIcon
                            size="md"
                            key={category.value}
                            plural
                            name={category.value}
                            isSelected={selectedCategory === category.value}
                            onClick={() => handleCategoryClick(category.value)}
                        />
                    ))}
                    {!isLoadingServiceGroups && !hideServiceGroups && (
                        <TagIcon
                            key="service_groups"
                            noBackground
                            rounded={false}
                            plural
                            size="sm"
                            className={`${selectedCategory === 'service_groups' ? 'font-medium text-gray-900' : ''} whitespace-nowrap flex-shrink-0`}
                            name="service_groups"
                            isSelected={selectedCategory === 'service_groups'}
                            onClick={() => handleCategoryClick('service_groups')}
                        />
                    )}
                    {isLoadingCategories && <Skeleton width="100%" height={40} />}
                </div>

                {!isLoadingCategories && selectedCategory && availability && (
                    <div className="w-fit mx-auto lg:flex items-center gap-10 solid-box-border mb-8">
                        <div className="flex-1">
                            <ServiceDatePopover
                                serviceCategory={selectedCategory}
                                limits={[reservation?.start_day, reservation?.end_day]}
                                value={dates}
                                onChange={(newDates) => {
                                    setDates(newDates);
                                    updateUrlParams({
                                        start: newDates[0],
                                        end: newDates[1],
                                    });
                                }}
                                size="sm"
                                className="bg-white p-2"
                            />
                        </div>
                        <div className="flex-1">
                            {reservation && (
                                <PaxSelector
                                    size="sm"
                                    updatedPersons={persons}
                                    persons={reservationPersons}
                                    onChange={(persons) => {
                                        setPersons(persons)
                                    }}
                                    className="font-bold pr-2"
                                />
                            )}
                            {!reservation && (
                                <NumberInput
                                    decimalSeparator="," // TODO internationalization
                                    thousandSeparator="." // TODO internationalization
                                    size="sm"
                                    value={pax_count}
                                    onChange={(value) => setPaxCount(value ? Number(value) : null)}
                                    hideControls
                                    leftSection={<IconUsersGroup stroke={1} size={16} color="gray" />}
                                    placeholder={t('paxs')}
                                    className="font-bold w-34"
                                    variant="unstyled"
                                />
                            )}
                        </div>
                        <Button
                            size="xs"
                            radius="xl"
                            color="var(--color-green)"
                            loading={isLoading || isFetching}
                            onClick={() => {
                                setSearchData({
                                    tenant_id,
                                    category: selectedCategory,
                                    start_day: dates[0],
                                    end_day: dates[1],
                                    pax_count: pax_count,
                                });
                                updateUrlParams({
                                    start: dates[0],
                                    end: dates[1],
                                    pax_count: pax_count?.toString() || undefined,
                                });
                            }}
                        >
                            <div className="flex items-center gap-2">
                                <IconSearch stroke={3} size={16} />
                                {t('search')}
                            </div>
                        </Button>
                    </div>
                )}
            </div>

            <div>
                {(isLoading || isFetching) && <SkeletonListWithFilters />}

                {/* All other categories */}
                {!(isLoading || isFetching) && selectedCategory && selectedCategory !== 'service_groups' && (
                    <ServiceTableGeneral
                        client={client}
                        publicPage={publicPage}
                        services={services}
                        dates={dates as [string, string]}
                        showAllSelfAttrs={showAllSelfAttrs}
                        onSelect={onSelect ? (service) => onSelect?.(service, persons, dates, null) : undefined}
                        sortBy="name"
                        excluded={['tourism_type', 'amenities', 'website', 'policies']}
                    />
                )}

                {/* Service Groups */}
                {!isLoadingServiceGroups && selectedCategory === 'service_groups' && !hideServiceGroups && (
                    <ServiceGroupsList
                        serviceGroups={serviceGroups}
                        onSelect={onSelect ? (service_links: ServiceServiceGroups[]) => {
                            service_links.forEach(link => onSelect?.(
                                link.service,
                                persons,
                                dates,
                                {
                                    discount_type: 'service_group',
                                    discount_id: link.id,
                                }
                            ))
                        } : undefined}
                    />
                )}
            </div>

        </>
    );
}